import React, { Fragment, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NavbarS2 from '../../components/NavbarS2/NavbarS2';
import PageTitle from '../../components/pagetitle/PageTitle';
import Footer from '../../components/footer/Footer';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Logo from '../../images/logo-2.svg';
import placeholder from '../../images/profile-photo-placeholder.svg';
import lineGreen from '../../images/service/liine-green.svg';
import Teams from '../../api/team';
import CtaJoinUs from '../../components/CtaJoinUs/CtaJoinUs';
import './CommitteePage.css';

// Volunteer Application Modal
const VolunteerModal = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        role: "",
        bio: "",
    });
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = "Name is required";
        if (!formData.email) newErrors.email = "Email is required";
        if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Email is invalid";
        if (!formData.bio) newErrors.bio = "Please tell us about yourself";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            setIsSubmitting(true);
            try {
                const response = await fetch('/api/volunteers', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData)
                });
                if (response.ok) {
                    setSuccessMessage("Thank you for applying! Your application will be reviewed by our team.");
                    setErrorMessage("");
                    setFormData({ name: "", email: "", phone: "", role: "", bio: "" });
                    setTimeout(() => {
                        onClose();
                        setSuccessMessage("");
                    }, 3000);
                } else {
                    const error = await response.json();
                    setErrorMessage(error.error || "Failed to submit application");
                }
            } catch (error) {
                setErrorMessage("Network error. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
        } else {
            setErrorMessage("Please fix the errors in the form.");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="volunteer-modal-overlay" onClick={onClose}>
            <div className="volunteer-modal" onClick={e => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>&times;</button>
                <h2>Become a Volunteer</h2>
                <p className="modal-subtitle">Join our community and make a difference!</p>

                {successMessage ? (
                    <div className="success-message">
                        <i className="flaticon-check"></i>
                        <p>{successMessage}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="form-row">
                            <div className="form-group">
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Full Name *"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={errors.name ? "error" : ""}
                                />
                                {errors.name && <small className="error-text">{errors.name}</small>}
                            </div>
                            <div className="form-group">
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Email *"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={errors.email ? "error" : ""}
                                />
                                {errors.email && <small className="error-text">{errors.email}</small>}
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder="Phone Number"
                                    value={formData.phone}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <select name="role" value={formData.role} onChange={handleChange}>
                                    <option value="">Select Role (Optional)</option>
                                    <option value="General Volunteer">General Volunteer</option>
                                    <option value="Event Coordinator">Event Coordinator</option>
                                    <option value="Youth Programs">Youth Programs</option>
                                    <option value="Education">Education</option>
                                    <option value="Community Outreach">Community Outreach</option>
                                    <option value="Media & Communications">Media & Communications</option>
                                    <option value="Facilities">Facilities</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>
                        <div className="form-group">
                            <textarea
                                name="bio"
                                placeholder="Tell us about yourself and why you'd like to volunteer... *"
                                value={formData.bio}
                                onChange={handleChange}
                                rows="4"
                                className={errors.bio ? "error" : ""}
                            />
                            {errors.bio && <small className="error-text">{errors.bio}</small>}
                        </div>
                        {errorMessage && <div className="error-message">{errorMessage}</div>}
                        <button type="submit" className="theme-btn submit-btn" disabled={isSubmitting}>
                            {isSubmitting ? 'Submitting...' : 'Submit Application'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

const ClickHandler = () => {
    window.scrollTo(10, 0);
}

const ITEMS_PER_ROW = 4;
const INITIAL_ROWS = 3;
const EXPANDED_ROWS = 5;

const PeopleSection = ({ title, subtitle, data, type, placeholder: placeholderImg, highlightFirst }) => {
    const [expanded, setExpanded] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const initialItems = INITIAL_ROWS * ITEMS_PER_ROW;
    const expandedItems = EXPANDED_ROWS * ITEMS_PER_ROW;
    const totalPages = Math.ceil(data.length / expandedItems);

    const getDisplayedItems = () => {
        if (!expanded) {
            return data.slice(0, initialItems);
        }
        const start = (currentPage - 1) * expandedItems;
        return data.slice(start, start + expandedItems);
    };

    const displayedItems = getDisplayedItems();
    const showMoreButton = !expanded && data.length > initialItems;
    const showPagination = expanded && totalPages > 1;

    // Separate first person if highlightFirst is true
    const firstPerson = highlightFirst && displayedItems.length > 0 ? displayedItems[0] : null;
    const otherPeople = highlightFirst ? displayedItems.slice(1) : displayedItems;

    return (
        <div className="people-section">
            <div className="row justify-content-center">
                <div className="col-lg-6 col-12">
                    <div className="section-title text-center">
                        <span style={{color: '#0D0A0B'}}>{subtitle}</span>
                        <h2>Our <span style={{position: 'relative', color: '#2A896F'}}>{title}
                            <img src={lineGreen} alt="" style={{position: 'absolute', left: 0, bottom: '-10px', width: '100%'}} />
                        </span></h2>
                    </div>
                </div>
            </div>
            {data.length === 0 ? (
                <div className="text-center py-4">
                    <p>No {title.toLowerCase()} yet.</p>
                </div>
            ) : (
                <>
                    {/* First person in centered row */}
                    {firstPerson && (
                        <div className="row committee-grid justify-content-center mb-4">
                            <div className="col-lg-3 col-md-4 col-6">
                                <div className="vol-card">
                                    <div className="image">
                                        <img src={firstPerson.photo_url || firstPerson.timg || placeholderImg} alt={firstPerson.name || firstPerson.title} />
                                        <span className="hover-icon"><i className="flaticon-share"></i></span>
                                        <ul>
                                            <li><Link onClick={ClickHandler} to="#"><i className="flaticon-camera"></i></Link></li>
                                            <li><Link onClick={ClickHandler} to="#"><i className="flaticon-facebook-app-symbol"></i></Link></li>
                                            <li><Link onClick={ClickHandler} to="#"><i className="flaticon-linkedin"></i></Link></li>
                                            <li><Link onClick={ClickHandler} to="#"><i className="flaticon-twitter"></i></Link></li>
                                        </ul>
                                    </div>
                                    <div className="text">
                                        <h3><Link onClick={ClickHandler} to={`/volunteer-single/${firstPerson.slug}`}>{firstPerson.name || firstPerson.title}</Link></h3>
                                        <span>{firstPerson.role || firstPerson.subtitle || type}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Other people */}
                    <div className="row committee-grid justify-content-center">
                        {otherPeople.map((person, index) => (
                            <div className="col-lg-3 col-md-4 col-6" key={person.id || index}>
                                <div className="vol-card">
                                    <div className="image">
                                        <img src={person.photo_url || person.timg || placeholderImg} alt={person.name || person.title} />
                                        <span className="hover-icon"><i className="flaticon-share"></i></span>
                                        <ul>
                                            <li><Link onClick={ClickHandler} to="#"><i className="flaticon-camera"></i></Link></li>
                                            <li><Link onClick={ClickHandler} to="#"><i className="flaticon-facebook-app-symbol"></i></Link></li>
                                            <li><Link onClick={ClickHandler} to="#"><i className="flaticon-linkedin"></i></Link></li>
                                            <li><Link onClick={ClickHandler} to="#"><i className="flaticon-twitter"></i></Link></li>
                                        </ul>
                                    </div>
                                    <div className="text">
                                        <h3><Link onClick={ClickHandler} to={`/volunteer-single/${person.slug}`}>{person.name || person.title}</Link></h3>
                                        <span>{person.role || person.subtitle || type}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {showMoreButton && (
                        <div className="text-center mt-4">
                            <button className="theme-btn" onClick={() => setExpanded(true)}>
                                Show More
                            </button>
                        </div>
                    )}
                    {showPagination && (
                        <div className="pagination-wrap text-center mt-4">
                            <button
                                className="pagination-btn"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span className="pagination-info">Page {currentPage} of {totalPages}</span>
                            <button
                                className="pagination-btn"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                            {expanded && (
                                <button className="pagination-btn collapse-btn" onClick={() => { setExpanded(false); setCurrentPage(1); }}>
                                    Show Less
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const PeoplePage = () => {
    const [volunteers, setVolunteers] = useState([]);
    const [patreons, setPatreons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await fetch('/api/volunteers');
            if (response.ok) {
                const data = await response.json();
                setVolunteers(data.filter(v => v.role !== 'Patreon'));
                setPatreons(data.filter(v => v.role === 'Patreon'));
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Fragment>
                <NavbarS2 hclass={'wpo-site-header'} Logo={Logo} />
                <PageTitle pageTitle={'Our People'} pagesub={'People'} />
                <div className="text-center py-5">
                    <p>Loading...</p>
                </div>
                <Footer />
            </Fragment>
        );
    }

    return (
        <Fragment>
            <NavbarS2 hclass={'wpo-site-header'} Logo={Logo} />
            <PageTitle pageTitle={'Our People'} pagesub={'People'} />
            <section className="committee-page section-padding">
                <div className="container">
                    <PeopleSection
                        title="Committee"
                        subtitle="Leadership Team"
                        data={Teams}
                        type="Committee Member"
                        placeholder={placeholder}
                        highlightFirst={true}
                    />

                    <div className="section-divider"></div>

                    <PeopleSection
                        title="Volunteers"
                        subtitle="Community Heroes"
                        data={volunteers}
                        type="Volunteer"
                        placeholder={placeholder}
                    />

                    <div className="section-divider"></div>

                    <PeopleSection
                        title="Patreons"
                        subtitle="Our Supporters"
                        data={patreons}
                        type="Patreon"
                        placeholder={placeholder}
                    />

                </div>
            </section>
            <CtaJoinUs onVolunteerClick={() => setShowModal(true)} />
            <VolunteerModal isOpen={showModal} onClose={() => setShowModal(false)} />
            <Footer />
            <Scrollbar />
        </Fragment>
    )
};

export default PeoplePage;
